import React, { useState, useEffect } from "react";
import { BarChart3, Target, Plus, ExternalLink, Hash, Info, Trash2, TrendingUp } from "lucide-react";
import { getFollowedCompetitors, addCompetitor, fetchCompetitorContent, FollowedCompetitor } from "../../services/competitorService";

interface Props {
  apiKey: string;
}

const CompetitorAnalysis: React.FC<Props> = ({ apiKey }) => {
  const [competitors, setCompetitors] = useState<FollowedCompetitor[]>([]);
  const [newCompName, setNewCompName] = useState("");
  const [newCompUrl, setNewCompUrl] = useState("");
  const [selectedCompId, setSelectedCompId] = useState<string>("");
  const [competitorVideos, setCompetitorVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const list = getFollowedCompetitors();
    setCompetitors(list);
    if (list.length > 0) setSelectedCompId(list[0].id);
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      if (!selectedCompId) return;
      setLoading(true);
      const content = await fetchCompetitorContent(apiKey, selectedCompId);
      setCompetitorVideos(content);
      setLoading(false);
    };
    loadContent();
  }, [selectedCompId, apiKey]);

  const handleAdd = () => {
    if (!newCompName || !newCompUrl) return;
    const added = addCompetitor(newCompName, newCompUrl);
    setCompetitors([...competitors, added]);
    setNewCompName("");
    setNewCompUrl("");
  };

  const chartCompetitors = [
    { name: "Tech Guru", subs: "1.2M", views: "45M", growth: "+12%", color: "#6366f1" },
    { name: "Code with Alex", subs: "850K", views: "28M", growth: "+8%", color: "#10b981" },
    { name: "Dev Insights", subs: "620K", views: "15M", growth: "+15%", color: "#f59e0b" },
  ];

  const contentGaps = [
    { topic: "Next.js 15 Deep Dive", demand: "High", competition: "Low", action: "Create Now" },
    { topic: "AI Agents with Python", demand: "Very High", competition: "Medium", action: "Investigate" },
    { topic: "Tailwind v4 First Look", demand: "Medium", competition: "Low", action: "Plan Video" },
  ];

  return (
    <div style={{ animation: "fadeIn 0.4s ease" }}>
      {/* TOP ROW: Benchmarking & Gaps (THE LAYOUT USER LIKED) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "32px" }}>
        {/* Benchmarking Section */}
        <div style={{ background: "#fff", padding: "28px", borderRadius: "20px", boxShadow: "0 10px 30px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "24px" }}>
             <BarChart3 size={24} color="#6366f1" />
             <h3 style={{ margin: 0, fontSize: "18px" }}>Niche Benchmarking</h3>
          </div>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {chartCompetitors.map((comp, idx) => (
              <div key={idx}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "13px" }}>
                  <span style={{ fontWeight: 600, color: "#1e293b" }}>{comp.name}</span>
                  <span style={{ color: comp.growth.startsWith("+") ? "#10b981" : "#ef4444", fontWeight: 700 }}>{comp.growth}</span>
                </div>
                <div style={{ height: "8px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ width: idx === 0 ? "85%" : idx === 1 ? "65%" : "45%", height: "100%", background: comp.color, borderRadius: "4px" }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Opportunity Gap Analysis */}
        <div style={{ background: "#fff", padding: "28px", borderRadius: "20px", boxShadow: "0 10px 30px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "24px" }}>
             <Target size={24} color="#f59e0b" />
             <h3 style={{ margin: 0, fontSize: "18px" }}>Content Opportunity Gaps</h3>
          </div>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {contentGaps.map((gap, idx) => (
              <div key={idx} style={{ padding: "12px 16px", background: "#fffcf5", border: "1px solid #fef3c7", borderRadius: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                   <p style={{ margin: 0, fontWeight: 700, fontSize: "13px", color: "#92400e" }}>{gap.topic}</p>
                   <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#b45309" }}>Demand: {gap.demand} | Competition: {gap.competition}</p>
                </div>
                <button style={{ padding: "4px 10px", borderRadius: "8px", background: "#92400e", color: "#fff", border: "none", fontSize: "10px", fontWeight: 600, cursor: "pointer" }}>
                  {gap.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "32px", alignItems: "start" }}>
        
        {/* Left: Tracker Sidebar */}
        <div style={{ background: "#fff", padding: "24px", borderRadius: "20px", boxShadow: "0 10px 30px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9" }}>
          <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Target size={18} color="#6366f1" /> Tracked Channels
          </h3>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" }}>
             {competitors.map(c => (
                <div 
                    key={c.id} 
                    onClick={() => setSelectedCompId(c.id)}
                    style={{ 
                        padding: "12px", 
                        borderRadius: "12px", 
                        background: selectedCompId === c.id ? "#eff6ff" : "transparent",
                        border: "1px solid",
                        borderColor: selectedCompId === c.id ? "#6366f1" : "transparent",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: 600, color: selectedCompId === c.id ? "#1e40af" : "#475569" }}>{c.name}</span>
                    <button style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer" }}><Trash2 size={14}/></button>
                </div>
             ))}
          </div>

          <div style={{ paddingTop: "20px", borderTop: "1px solid #f1f5f9" }}>
             <p style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 700, color: "#64748b" }}>ADD NEW COMPETITOR</p>
             <input 
                placeholder="Channel Name"
                value={newCompName}
                onChange={e => setNewCompName(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "8px", fontSize: "13px" }}
             />
             <input 
                placeholder="URL or @Handle"
                value={newCompUrl}
                onChange={e => setNewCompUrl(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "12px", fontSize: "13px" }}
             />
             <button 
                onClick={handleAdd}
                style={{ width: "100%", padding: "12px", borderRadius: "10px", background: "#6366f1", color: "#fff", border: "none", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
             >
                <Plus size={16} /> Track Channel
             </button>
          </div>
        </div>

        {/* Right: Deep Content Discovery */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
           <div style={{ background: "#fff", padding: "24px", borderRadius: "20px", boxShadow: "0 10px 30px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                 <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}><BarChart3 size={20} color="#6366f1" /> Recent Upload Strategy</h3>
                 {loading && <span style={{ fontSize: "12px", color: "#64748b" }}>Broadcasting deep analysis...</span>}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                 {competitorVideos.map(video => (
                    <div key={video.id} style={{ padding: "20px", borderRadius: "16px", background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                       <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", marginBottom: "16px" }}>
                          <img src={video.thumbnail} style={{ width: "140px", borderRadius: "8px" }} alt="" />
                          <div style={{ flex: 1 }}>
                             <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>{video.title}</p>
                                <a href={`https://youtube.com/watch?v=${video.id}`} target="_blank" rel="noreferrer" style={{ color: "#6366f1" }}><ExternalLink size={16}/></a>
                             </div>
                             <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>Published {new Date(video.publishedAt).toLocaleDateString()}</p>
                             <div style={{ display: "flex", gap: "12px", marginTop: "12px" }}>
                                <span style={{ fontSize: "12px", fontWeight: 700, color: "#10b981" }}>👁️ {parseInt(video.viewCount || "0").toLocaleString()} views</span>
                             </div>
                          </div>
                       </div>

                       <div style={{ background: "rgba(255,255,255,0.6)", padding: "16px", borderRadius: "12px", border: "1px dotted #e2e8f0" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                             <Hash size={14} color="#6366f1" />
                             <span style={{ fontSize: "11px", fontWeight: 800, color: "#6366f1", textTransform: "uppercase" }}>Video Tags</span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                             {video.tags.map((tag: string) => (
                                <span key={tag} style={{ background: "#fff", padding: "4px 10px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "11px", color: "#475569" }}>{tag}</span>
                             ))}
                             {video.tags.length === 0 && <span style={{ fontSize: "11px", color: "#94a3b8" }}>No tags found.</span>}
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "16px", marginBottom: "8px" }}>
                             <Info size={14} color="#6366f1" />
                             <span style={{ fontSize: "11px", fontWeight: 800, color: "#6366f1", textTransform: "uppercase" }}>Strategy Description</span>
                          </div>
                          <p style={{ margin: 0, fontSize: "12px", color: "#475569", lineHeight: "1.6", maxHeight: "80px", overflow: "hidden", textOverflow: "ellipsis" }}>{video.description}</p>
                       </div>
                    </div>
                 ))}
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorAnalysis;
